import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/lib/require-supabase-auth";

/** Throws if the caller is not an admin. */
export async function assertAdmin(supabase: any, userId: string): Promise<void> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin role required");
}

/** Staff who are granted admin automatically on first verified sign-in. */
const ADMIN_EMAIL_ALLOWLIST = [
  "amandasowadski@gmail.com",
  "laura.skindaraite@restorehair.com",
];

export const isCurrentUserAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    if (data) return { isAdmin: true };

    const claims = context.claims as Record<string, unknown>;
    const email = typeof claims?.["email"] === "string" ? (claims["email"] as string).toLowerCase() : null;
    // Require a proven-verified email: a missing/unknown claim is NOT treated
    // as verified, otherwise anyone signing up with a staff address would be
    // granted admin before confirming the mailbox.
    const verified =
      claims?.["email_verified"] === true ||
      claims?.["email_verified"] === "true";
    if (email && verified && ADMIN_EMAIL_ALLOWLIST.includes(email)) {
      const { createSupabaseAdmin } = await import("@/lib/supabase-admin.server");
      const supabaseAdmin = createSupabaseAdmin();
      await supabaseAdmin
        .from("user_roles")
        .upsert({ user_id: context.userId, role: "admin" }, { onConflict: "user_id,role" });
      return { isAdmin: true };
    }

    return { isAdmin: false };
  });

export const requireAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    return { ok: true, userId: context.userId };
  });